import { useEffect, useMemo, useRef, useState } from "react";
import { Button, ConfirmDialog, DiamondCheckbox, SearchField } from "@legends/ui";
import type { ColDef } from "ag-grid-community";
import DataGrid from "./DataGrid";
import SearchableSelect from "./admin/SearchableSelect";
import {
  archiveLegend,
  createLegend,
  deleteLegend,
  fetchAdminLegends,
  fetchCaves,
  linkLegendCave,
  removeLegendPhoto,
  setLegendFeatured,
  unarchiveLegend,
  uploadLegendPhoto,
  type AdminLegend,
  type Cave,
} from "./api";
import { Section, alertStyle, errMsg, monoLabelStyle } from "./sections";

// Super-admin legend curator (ADR 0021), on the Cave-themed AG Grid (ADR 0156): add a
// legend via the pinned top row, edit its cave / featured flag inline, filter + sort.
// User↔legend binding lives on the richer Mapping tab (LSC-296), which shows the current
// user per legend; here we keep create + cave + featured, plus the legend lifecycle
// (ADR 0162): an inline "Archived" toggle retires a legend, and the ✕ hard-deletes a
// PRISTINE one (no cave/account link — a 409 with archive guidance otherwise).

type Row = AdminLegend;

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export default function LegendsAdminScreen() {
  const [rows, setRows] = useState<Row[]>([]);
  const [caves, setCaves] = useState<Cave[]>([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Headshot management (LSC-407): pick a legend, upload/remove its portrait.
  const [photoId, setPhotoId] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState<string | null>(null);
  const photoFileRef = useRef<HTMLInputElement>(null);

  const load = (withArchived = showArchived) =>
    fetchAdminLegends("", withArchived).then(setRows).catch((e) => setError(errMsg(e)));
  useEffect(() => {
    void load(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);
  useEffect(() => {
    fetchCaves()
      .then((cs) => setCaves(cs ?? []))
      .catch(() => {});
  }, []);

  const caveIds = useMemo(() => ["", ...caves.map((c) => c.id)], [caves]);
  const caveName = useMemo(() => {
    const m = new Map(caves.map((c) => [c.id, c.name] as const));
    return (id: string) => (id ? m.get(id) ?? id : "— none —");
  }, [caves]);

  const columns = useMemo<ColDef<Row>[]>(
    () => [
      { field: "name", headerName: "Legend", minWidth: 160 },
      { field: "sport", headerName: "Sport", minWidth: 110, valueFormatter: (p) => cap(String(p.value ?? "")) },
      { field: "level", headerName: "Level", minWidth: 100 },
      { field: "team", headerName: "Team", minWidth: 150 },
      {
        field: "caveId",
        headerName: "Cave",
        minWidth: 150,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: caveIds },
        valueFormatter: (p) => caveName(String(p.value ?? "")),
      },
      {
        field: "featured",
        headerName: "Featured",
        width: 120,
        maxWidth: 130,
        editable: true,
        filter: false,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
      },
      {
        // Inline archive toggle (ADR 0162): check to retire, uncheck to restore. Only
        // meaningful in the Show-archived view; hidden otherwise to keep the roster clean.
        field: "archived",
        headerName: "Archived",
        width: 120,
        maxWidth: 130,
        hide: !showArchived,
        editable: true,
        filter: false,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
      },
    ],
    [caveIds, caveName, showArchived],
  );

  const onEdit = async (row: Row, field: string, value: unknown) => {
    setError(null);
    try {
      if (field === "caveId") {
        const caveId = String(value ?? "");
        await linkLegendCave(row.id, caveId);
        setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, caveId } : r)));
      } else if (field === "featured") {
        await setLegendFeatured(row.id, Boolean(value));
        setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, featured: Boolean(value) } : r)));
      } else if (field === "archived") {
        if (value) await archiveLegend(row.id);
        else await unarchiveLegend(row.id);
        await load();
      }
    } catch (e) {
      setError(errMsg(e));
      void load();
    }
  };

  const onAdd = async (draft: Row) => {
    if (!draft.name?.trim() || !draft.sport?.trim()) {
      setError("Name and sport are required.");
      return false;
    }
    setError(null);
    try {
      const legend = await createLegend({
        name: draft.name.trim(),
        sport: draft.sport.trim(),
        level: (draft.level ?? "").trim(),
        team: (draft.team ?? "").trim(),
        caveId: draft.caveId?.trim() || undefined,
        featured: Boolean(draft.featured),
      });
      setRows((rs) => [legend, ...rs]);
    } catch (e) {
      setError(errMsg(e));
      return false;
    }
  };

  const askDelete = (row: Row) => {
    setDeleteError(null);
    setPendingDelete(row);
  };
  const cancelDelete = () => {
    if (deleteBusy) return;
    setPendingDelete(null);
    setDeleteError(null);
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteLegend(pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (e: unknown) {
      // Surface the server reason (e.g. a 409 "unlink or archive instead") inside the
      // modal so the operator sees why a linked legend can't be deleted.
      setDeleteError(errMsg(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  const onPhotoFile = async (file: File | undefined) => {
    if (!file || !photoId) return;
    setPhotoMsg(null);
    setError(null);
    setPhotoBusy(true);
    try {
      const photo = await uploadLegendPhoto(photoId, file);
      setRows((rs) => rs.map((r) => (r.id === photoId ? { ...r, photo } : r)));
      setPhotoMsg("Headshot updated.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setPhotoBusy(false);
      if (photoFileRef.current) photoFileRef.current.value = ""; // allow re-picking the same file
    }
  };
  const onPhotoRemove = async () => {
    if (!photoId) return;
    setPhotoMsg(null);
    setError(null);
    setPhotoBusy(true);
    try {
      await removeLegendPhoto(photoId);
      setRows((rs) => rs.map((r) => (r.id === photoId ? { ...r, photo: "" } : r)));
      setPhotoMsg("Headshot removed — showing initials.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const photoLegend = rows.find((r) => r.id === photoId) ?? null;
  const legendPickerOptions = useMemo(() => rows.map((r) => ({ value: r.id, label: r.name })), [rows]);

  const addTemplate: Row = { id: "", name: "", position: "", years: "", initials: "", photo: "", caveId: "", sport: "", featured: false, level: "", team: "" };
  const featuredCount = rows.filter((r) => r.featured).length;

  return (
    <div>
      <h1 style={{ margin: 0, ...monoLabelStyle, fontWeight: 600 }}>Legends · Featured</h1>
      <p style={{ margin: "10px 0 14px", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
        Add a legend with <strong>＋ Add a legend</strong>; edit its cave or featured flag inline. Featured legends
        appear on the Selector. A pristine legend (no cave, no linked account) can be <strong>deleted</strong> with the
        ✕; once it headlines a cave or maps to an account it must be <strong>archived</strong> instead — retired from
        discovery while its record is kept.
      </p>
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
      <div style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SearchField value={q} onChange={setQ} placeholder="Filter legends…" width={320} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", ...monoLabelStyle, color: "var(--text-dim)" }}>
          <DiamondCheckbox checked={showArchived} onChange={() => setShowArchived((v) => !v)} aria-label="Show archived legends" />
          Show archived
        </label>
        <span style={{ ...monoLabelStyle, marginLeft: "auto" }}>
          {featuredCount} featured · {rows.length} legends
        </span>
      </div>
      <Section title="Headshots">
        <p style={{ margin: "0 0 10px", fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
          Upload a portrait for a legend — it shows on the Selector and in the native apps. A legend with no headshot
          falls back to its initials. Pick a legend, then choose a PNG/JPEG/WebP.
        </p>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ minWidth: 260 }}>
            <label style={{ ...monoLabelStyle, display: "block", marginBottom: 4 }}>Legend</label>
            <SearchableSelect
              ariaLabel="Legend to set a headshot for"
              options={legendPickerOptions}
              value={photoId}
              onChange={(v) => { setPhotoId(v); setPhotoMsg(null); }}
              placeholder="Search legends…"
            />
          </div>
          {photoLegend && (
            <>
              <div aria-label="Current headshot" style={{ width: 72, height: 96, borderRadius: 8, overflow: "hidden", background: "var(--surface-2, #222)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none", border: "1px solid var(--border-subtle, #333)" }}>
                {photoLegend.photo ? (
                  <img src={photoLegend.photo} alt={`${photoLegend.name} headshot`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ ...monoLabelStyle, fontSize: 20, color: "var(--text-dim)" }}>{photoLegend.initials}</span>
                )}
              </div>
              <input
                ref={photoFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="Headshot image file"
                disabled={photoBusy}
                onChange={(e) => void onPhotoFile(e.target.files?.[0])}
                style={{ fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}
              />
              {photoLegend.photo && (
                <Button variant="muted" onClick={() => void onPhotoRemove()} disabled={photoBusy} aria-label={`Remove ${photoLegend.name} headshot`}>
                  Remove
                </Button>
              )}
            </>
          )}
        </div>
        {photoMsg && <p style={{ ...monoLabelStyle, color: "var(--accent)", margin: "8px 0 0" }}>{photoMsg}</p>}
      </Section>
      <DataGrid<Row>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        onEdit={onEdit}
        onDelete={askDelete}
        onAdd={onAdd}
        addTemplate={addTemplate}
        addModal
        addLabel="a legend"
        paginated
        quickFilterText={q}
        label="Legends"
        emptyText="No legends yet — add one in the top row."
      />
      {pendingDelete && (
        <ConfirmDialog
          title={`Delete ${pendingDelete.name}?`}
          destructive
          confirmLabel="Delete legend"
          busy={deleteBusy}
          error={deleteError ?? undefined}
          onConfirm={() => void confirmDelete()}
          onCancel={cancelDelete}
        >
          This permanently removes the legend. A legend that headlines a cave or maps to an account can&rsquo;t be
          deleted — archive it instead.
        </ConfirmDialog>
      )}
    </div>
  );
}
